import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Hub} from 'yougile-model'
import {Accordion} from '@components/common/base/accordion'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {crmStore} from '@components/crm/crm-store'
import {ParentTask} from '@components/right-panel/checklists-tab/subtasks-new/parent-tasks/parent-task'
import {StoreContext} from '@components/right-panel/checklists-tab/subtasks-new/parent-tasks/parent-tasks-store'
import {ContactFields} from '@components/right-panel/crm/default-fields/contact-fields'
import {OrganizationFields} from '@components/right-panel/crm/default-fields/organization-fields'
import {SubtaskUtil} from '@extensions/subtask-util'
import {Task} from '@extensions/task'

const TwDealBadge = tw.span`
  inline-flex
  items-center
  justify-center
  bg-background-secondary
  text-secondary
  px-4
  rounded-4
  text-xs-semibold
  uppercase
`

const TwText = tw.div`
  text-sm-regular
  text-primary
`

const TitleWithBanner = ({title}: {title: string}) => {
  return (
    <div className="flex gap-4">
      <TwDealBadge>{Lang.msg.crm.dealBadge}</TwDealBadge>
      <TwText>{title}</TwText>
    </div>
  )
}

export const DefaultFieldsPanel = ({deal}: {deal: Hub | undefined}) => {
  if (!deal) {
    return null
  }

  const subtasks = SubtaskUtil.getSubtaskIds(deal)
    .map(Task.getById)
    .filter((x): x is Hub => !!x)

  const role = crmStore.getCrmRoleOfHub(deal)

  const fieldsGroups = []

  if (role === 'contact') {
    fieldsGroups.push({
      id: 'contactFieldsGroup',
      title: (
        <strong>{Lang.msg.crm.rightPanel.contactPerson.contactFields}</strong>
      ),
      content: <ContactFields deal={deal} />
    })
  }

  if (role === 'organization') {
    fieldsGroups.push({
      id: 'organizationFieldsGroup',
      title: (
        <strong>
          {Lang.msg.crm.rightPanel.organization.organizationFields}
        </strong>
      ),
      content: <OrganizationFields deal={deal} />
    })
  }

  const items = [
    ...fieldsGroups,
    {
      id: 'childrenTasks',
      title: <strong>{Lang.msg.crm.rightPanel.childTasks}</strong>,
      content: (
        <div className="pt-8">
          {subtasks.length > 0 ? (
            subtasks.map(subtask => (
              <ParentTask
                key={subtask.id}
                id={subtask.id}
                title={
                  subtask.title ? (
                    <TitleWithBanner title={subtask.title} />
                  ) : null
                }
                isParent={SubtaskUtil.getSubtaskIds(subtask).length > 0}
                isCompleted={subtask.data?.gtd?.state?.stage === 'done'}
                isDeal
              />
            ))
          ) : (
            <div className="pl-16 pb-8 text-secondary">
              {Lang.msg.crm.rightPanel.noChildTasks}
            </div>
          )}
        </div>
      )
    }
  ]

  const defaultOpenedIds = React.useMemo(() => {
    const ids = []
    if (role === 'contact') {
      ids.push('contactFieldsGroup')
    }
    if (role === 'organization') {
      ids.push('organizationFieldsGroup')
    }
    ids.push('childrenTasks')
    return ids
  }, [deal.id, role])

  return (
    <StoreContext.Provider
      value={
        {
          state: {opened: new Set()},
          toggleOpen: () => {}
        } as any
      }
    >
      <div className="flex flex-col h-full min-h-0">
        <WithPerfectScrollbar
          className="flex-1 min-h-0"
          options={{suppressScrollX: true}}
        >
          <form onSubmit={e => e.preventDefault()} noValidate>
            <div className="px-24 py-16 space-y-8">
              <Accordion
                multi
                defaultOpenedIds={defaultOpenedIds}
                headerStyle="filled"
                items={items}
              />
            </div>
          </form>
        </WithPerfectScrollbar>
      </div>
    </StoreContext.Provider>
  )
}
